import {
  AllCarouselItems,
  ApiBaseUrls,
  AppDefaultHostnames,
  AzureBaseUrls,
  CompanyProfiles,
} from "@/constants/appSettings";
import {
  BREAKPOINT_LARGE_MIN,
  BREAKPOINT_MOBILE_MAX,
  BREAKPOINT_SMALL_MAX,
  BREAKPOINT_TABLET_MAX,
} from "@/constants/breakpoints";
import { HeightFabButton, StatusBarCheckTimeout } from "@/constants/global";
import { STATUS_BAR_DEFAULT_STYLE } from "@/constants/statusBar";
import { AppNamesEnum, EnvironmentNamesEnum } from "@/enums/appSettings";
import { StatusBarStyleEnum } from "@/enums/global";
import { isAppVersionOutdated } from "@/methods/appUtils";
import { setStatusBarStyle as applyNativeStatusBarStyle } from "@/methods/statusBar";
import { getImageUrl, gotoLogout } from "@/methods/utils";
import { useServerClockStore } from "@/stores/core/serverClockStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import {
  AppSettingsStore,
  AppVersionInfo,
  CompanyProfile,
  DeviceSafeAreaInsets,
  NavbarMenu,
} from "@/types/global";
import { SafeArea } from "@capacitor-community/safe-area";
import { App } from "@capacitor/app";
import { Camera } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { OrientationLockType, ScreenOrientation } from "@capacitor/screen-orientation";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { differenceInSeconds } from "date-fns";
import { defineStore, storeToRefs } from "pinia";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";

const MAX_RELOAD_ATTEMPTS = 3;

// Global scroll callbacks registry - module level (not in state, doesn't need reactivity)
// This is used to trigger all registered scroll callbacks via store
const globalScrollCallbacks = new Set<(event: any) => void>();

function getInitialAppSettingsStore(): AppSettingsStore {
  const defaultDeviceControls = {
    AllowScreenRotate: false,
    AllowScreenCast: false,
    AllowFullscreen: false,
  };
  const initialAppSettingsStore: AppSettingsStore = {
    LatestAppVersion: null,
    SkippedAppVersion: null,
    AppMenu: null,
    AppMenuKey: 0,
    RouterOutletKey: 0,
    AppReloadAttempts: 0,
    LastReloadAttemptedOn: null,
    IsCustomDomain: false,
    CustomAssets: null,
    HideNavbar: false,
    ShowAppFooter: true,
    HideNavbarOnScroll: false,
    IsLargeScreen: false,
    IsTabletScreen: false,
    IsMobileScreen: false,
    IsBelowSmallBreakpoint: false,
    IsAppInForeground: true,
    IsAppFullscreen: false,
    IsKeyboardOpen: false,
    AppScreenOrientation: "portrait",
    CameraPermissions: null,
    DeviceControls: defaultDeviceControls,
    DeviceInsets: null,
    ScrollPosition: 0,
    ShowAppExitAlert: false,
    StatusBarCheckIntervalId: null,
    ClientNativeVersion: null,
    IsOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    StatusBarStyle: STATUS_BAR_DEFAULT_STYLE,
  };
  return JSON.parse(JSON.stringify(initialAppSettingsStore));
}

export const useAppSettingsStore = defineStore("appSettingsStore", {
  state: (): AppSettingsStore => getInitialAppSettingsStore(),
  getters: {
    AppName(): AppNamesEnum {
      return import.meta.env.VITE_APP_NAME as AppNamesEnum;
    },
    AppEnvironment(): string {
      return import.meta.env.VITE_APP_ENVIRONMENT;
    },
    IsProductionEnv(): boolean {
      return import.meta.env.MODE === EnvironmentNamesEnum.PRODUCTION;
    },
    AppHostName(): string {
      const appHostnames = AppDefaultHostnames[this.AppName as keyof typeof AppDefaultHostnames];
      return appHostnames[this.AppEnvironment as keyof typeof appHostnames];
    },
    ApiBaseUrl(): string {
      const apiBaseUrls = ApiBaseUrls[this.AppName as keyof typeof ApiBaseUrls];
      const baseUrl = apiBaseUrls[this.AppEnvironment as keyof typeof apiBaseUrls];
      return `${baseUrl}/v${import.meta.env.VITE_API_MAJOR_VERSION}`;
    },
    AzureBaseUrl(): string {
      const azureBaseUrls = AzureBaseUrls[this.AppEnvironment as keyof typeof AzureBaseUrls];
      return azureBaseUrls;
    },
    ClientWebVersion(): string {
      return import.meta.env.VITE_APP_VERSION;
    },
    AppUpdateRequired(): boolean {
      if (this.LatestAppVersion === null) {
        return false;
      }
      return isAppVersionOutdated(this.ClientWebVersion, this.LatestAppVersion.AppVersion);
    },
    IsForcedAppUpdate(): boolean {
      if (this.LatestAppVersion === null) {
        return false;
      }
      return isAppVersionOutdated(this.ClientWebVersion, this.LatestAppVersion.MinSupportedVersion);
    },
    IsLiveUpdateAvailable(): boolean {
      if (
        this.LatestAppVersion === null ||
        !this.AppUpdateRequired ||
        !this.LatestAppVersion.IsLiveUpdate
      ) {
        return false;
      }
      if (!this.IsNativePlatform) {
        return false;
      }
      if (this.AppEnvironment !== EnvironmentNamesEnum.PRODUCTION) {
        return true;
      }
      if (this.ClientNativeVersion === null) {
        return false;
      }
      return !isAppVersionOutdated(
        this.ClientNativeVersion,
        this.LatestAppVersion.MinNativeVersion,
      );
    },
    CompanyProfile(): CompanyProfile {
      return CompanyProfiles[this.AppName as keyof typeof CompanyProfiles];
    },
    AppLogo(): string {
      const appLogoFile = `${this.AppName.toLowerCase()}-logo.svg`;
      return getImageUrl(appLogoFile) as string;
    },
    AppLogoHorizontal(): string {
      const appLogoFile = `${this.AppName.toLowerCase()}-logo-horizontal.svg`;
      return getImageUrl(appLogoFile) as string;
    },
    AffiliationLogo(): string | null {
      if (!this.IsCustomDomain) {
        return this.AppLogo;
      }
      return this.CustomAssets && this.CustomAssets.AffiliationLogo
        ? this.CustomAssets.AffiliationLogo
        : null;
    },
    DashboardLogo(): string {
      if (this.CustomAssets && this.CustomAssets.DashboardLogo) {
        return this.CustomAssets.DashboardLogo;
      }
      const appLogoFile = `${this.AppName.toLowerCase()}-logo-horizontal.svg`;
      return getImageUrl(appLogoFile) as string;
    },
    DashboardLogoWhite(): string {
      if (this.CustomAssets && this.CustomAssets.DashboardLogo) {
        return this.CustomAssets.DashboardLogo;
      }
      const appLogoFile = `${this.AppName.toLowerCase()}-logo-white-horizontal.svg`;
      return getImageUrl(appLogoFile) as string;
    },
    LandingPageHeaderImage(): string {
      const imageFile = `${this.AppName.toLowerCase()}-landing-page-header.svg`;
      return getImageUrl(imageFile) as string;
    },
    CarouselItems(): Array<any> {
      return AllCarouselItems[this.AppName as keyof typeof AllCarouselItems];
    },
    IsNativePlatform(): boolean {
      return Capacitor.isNativePlatform();
    },
    DevicePlatform(): string {
      return Capacitor.getPlatform();
    },
    ShowDeviceControls(): boolean {
      return (
        this.DeviceControls.AllowScreenRotate ||
        this.DeviceControls.AllowFullscreen ||
        this.DeviceControls.AllowScreenCast
      );
    },
    IsCameraAllowed(): boolean {
      return this.CameraPermissions !== null && this.CameraPermissions.camera === "granted";
    },
    IsCameraDenied(): boolean {
      return this.CameraPermissions !== null && this.CameraPermissions.camera === "denied";
    },
    IsOffline(state): boolean {
      return !state.IsOnline;
    },
  },
  actions: {
    async initializeAppState() {
      this.setHeightFabButton();
      this.addWindowResizeListener();
      await this.addAppStateChangeListener();
      await this.addAppFullscreenListener();
      this.addKeyboardShowListener();
      await this.readClientNativeVersion();
      this.initializeNetworkListener();
    },
    initializeNetworkListener() {
      if (typeof window === "undefined") {
        return;
      }
      // Seed from live navigator.onLine at every hydration (BR-061).
      // Listeners are app-lifetime (BR-062) — no removeEventListener call.
      this.IsOnline = navigator.onLine;
      window.addEventListener("online", () => {
        this.IsOnline = true;
      });
      window.addEventListener("offline", () => {
        this.IsOnline = false;
      });
    },
    async setStatusBarStyle(style: StatusBarStyleEnum) {
      this.StatusBarStyle = style;
      try {
        await applyNativeStatusBarStyle(style);
      } catch {
        // BR-028: swallow native StatusBar rejection — a status-bar failure
        // must never break navigation. Helper itself is already web-safe +
        // plugin-availability-guarded (BR-110, BR-113); this catch covers
        // edge cases like a mid-call plugin teardown on a stale native shell.
      }
    },
    async readClientNativeVersion() {
      if (!this.IsNativePlatform) {
        return;
      }
      try {
        const info = await App.getInfo();
        this.ClientNativeVersion = info.version;
      } catch (error: any) {
        this.ClientNativeVersion = null;
        console.warn(`App.getInfo() failed; ClientNativeVersion left null: ${error.message}`);
      }
    },
    async initializeAppConfig() {
      this.LatestAppVersion = null;
      this.SkippedAppVersion = null;
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.getLatestAppVersionInfo(this.AppName);
      if (!response.isSuccess) {
        throw new Error(response.errorMessage || "Failed to load app version");
      }
      this.LatestAppVersion = response.data as AppVersionInfo;
      this.IsCustomDomain =
        window.location.hostname !== "localhost" && window.location.hostname !== this.AppHostName;
      if (this.IsCustomDomain) {
        await this.updateCustomAssets();
      }
      return;
    },
    async getNavbarMenu() {
      if (this.AppMenu !== null) {
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { appSettingsApi } = apiServicesStore;
      const response = await appSettingsApi.getNavbarMenu();
      if (response.isSuccess) {
        this.AppMenu = response.data as NavbarMenu;
        this.AppMenuKey += 1;
      } else {
        await gotoLogout(true);
      }
    },
    async updateCustomAssets() {
      if (
        this.CustomAssets !== null &&
        this.CustomAssets.CustomDomain === window.location.hostname
      ) {
        return;
      }
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = storeToRefs(apiServicesStore);
      const response = await unverifiedUserApi.value.getAffiliationCustomAssets(
        window.location.hostname,
      );
      const defaultCustomAssets = {
        CustomDomain: null,
        EmailId: null,
        PhoneNumber: null,
        AffiliationLogo: null,
        DashboardLogo: null,
        CarouselImages: [
          getImageUrl("custom-domain-carousel-1.svg"),
          getImageUrl("custom-domain-carousel-2.svg"),
          getImageUrl("custom-domain-carousel-3.svg"),
        ],
      };
      this.CustomAssets = response.isSuccess ? response.data : defaultCustomAssets;
    },
    addWindowResizeListener() {
      const largeMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINT_LARGE_MIN}px)`);
      this.IsLargeScreen = largeMediaQuery.matches;
      largeMediaQuery.addEventListener("change", (e) => {
        this.IsLargeScreen = e.matches;
      });
      const tabletMediaQuery = window.matchMedia(`(max-width: ${BREAKPOINT_TABLET_MAX}px)`);
      this.IsTabletScreen = tabletMediaQuery.matches;
      tabletMediaQuery.addEventListener("change", (e) => {
        this.IsTabletScreen = e.matches;
      });
      const mobileMediaQuery = window.matchMedia(`(max-width: ${BREAKPOINT_MOBILE_MAX}px)`);
      this.IsMobileScreen = mobileMediaQuery.matches;
      mobileMediaQuery.addEventListener("change", (e) => {
        this.IsMobileScreen = e.matches;
      });
      const belowSmallMediaQuery = window.matchMedia(`(max-width: ${BREAKPOINT_SMALL_MAX}px)`);
      this.IsBelowSmallBreakpoint = belowSmallMediaQuery.matches;
      belowSmallMediaQuery.addEventListener("change", (e) => {
        this.IsBelowSmallBreakpoint = e.matches;
      });
    },
    async addAppStateChangeListener() {
      await App.addListener("appStateChange", async (state) => {
        if (state.isActive) {
          const serverClockStore = useServerClockStore();
          const { pingServerAndInitializeClock } = serverClockStore;
          await pingServerAndInitializeClock();
          this.IsAppInForeground = true;
        } else {
          this.IsAppInForeground = false;
          await this.deactivateAppFullscreen();
        }
      });
    },
    async addAppFullscreenListener() {
      // Handle fullscreen change on desktop browsers when user exits
      // fullscreen mode by clicking the escape key
      document.addEventListener("fullscreenchange", async () => {
        if (!document.fullscreenElement && this.IsAppFullscreen) {
          await this.handleAppFullScreenChange(false);
        }
      });
    },
    async activateAppFullscreen() {
      await this.handleAppFullScreenChange(true);
    },
    async deactivateAppFullscreen() {
      if (this.IsAppFullscreen === false) {
        return;
      }
      await this.handleAppFullScreenChange(false);
    },
    async toggleAppFullscreen() {
      await this.handleAppFullScreenChange(!this.IsAppFullscreen);
    },
    async handleAppFullScreenChange(fullscreenState: boolean) {
      this.IsAppFullscreen = fullscreenState;
      if (this.IsAppFullscreen) {
        if (document.documentElement.requestFullscreen) {
          try {
            await document.documentElement.requestFullscreen();
          } catch (err: any) {
            console.warn(`Error attempting to enable full-screen mode: ${err.message}`);
          }
        }
        if (this.IsNativePlatform) {
          try {
            await SafeArea.hideSystemBars({ type: undefined });
            this.StatusBarCheckIntervalId = setInterval(async () => {
              await SafeArea.hideSystemBars({ type: undefined });
            }, StatusBarCheckTimeout);
          } catch (err: any) {
            console.warn(`Error attempting to hide status bar: ${err.message}`);
          }
        }
        this.toggleIonicSafeAreaInsets({ Top: "0px", Bottom: "0px", Left: "0px", Right: "0px" });
      } else {
        if (document.fullscreenElement) {
          try {
            await document.exitFullscreen();
          } catch (err: any) {
            console.warn(`Error attempting to exit full-screen mode: ${err.message}`);
          }
        }
        if (this.IsNativePlatform) {
          if (this.StatusBarCheckIntervalId !== null) {
            clearInterval(this.StatusBarCheckIntervalId);
            this.StatusBarCheckIntervalId = null;
          }
          try {
            await SafeArea.showSystemBars({ type: undefined });
          } catch (err: any) {
            console.warn(`Error attempting to show status bar: ${err.message}`);
          }
        }
        this.toggleIonicSafeAreaInsets(this.DeviceInsets);
      }
    },
    async toggleAppScreenOrientation() {
      if (!(this.IsNativePlatform && this.DeviceControls.AllowScreenRotate)) {
        return;
      }
      const newOrientation = this.AppScreenOrientation.startsWith("portrait")
        ? this.AppScreenOrientation.replace("portrait", "landscape")
        : this.AppScreenOrientation.replace("landscape", "portrait");
      await this.setAppScreenOrientation(newOrientation);
    },
    async setAppScreenOrientation(newOrientation?: string) {
      if (!this.IsNativePlatform) {
        return;
      }
      if (newOrientation === undefined) {
        newOrientation = "portrait";
      }
      await ScreenOrientation.lock({ orientation: newOrientation as OrientationLockType });
      const orientation = await ScreenOrientation.orientation();
      this.AppScreenOrientation = orientation.type;
      this.getIonicSafeAreaInsets();
      this.toggleIonicSafeAreaInsets(this.DeviceInsets);
    },
    addKeyboardShowListener() {
      if (!this.IsNativePlatform) {
        return;
      }
      Keyboard.addListener("keyboardWillShow", () => {
        this.IsKeyboardOpen = true;
      });
      Keyboard.addListener("keyboardWillHide", () => {
        this.IsKeyboardOpen = false;
      });
    },
    async onEnableCastingClicked() {
      NativeSettings.open({ optionAndroid: AndroidSettings.Cast, optionIOS: IOSSettings.App });
    },
    async handleVitePreloadError() {
      const serverClockStore = useServerClockStore();
      const { UpdatedServerClock } = storeToRefs(serverClockStore);
      await this.resetReloadAttemptsAfterLag(UpdatedServerClock.value);
      if (this.AppReloadAttempts < MAX_RELOAD_ATTEMPTS) {
        console.warn("Vite preload error detected: reloading the page!");
        console.warn(`Reload Attempt: ${this.AppReloadAttempts}/${MAX_RELOAD_ATTEMPTS}`);
        this.AppReloadAttempts++;
        this.LastReloadAttemptedOn = UpdatedServerClock.value;
        window.location.reload();
      } else {
        console.warn(`Max reload attempts (${MAX_RELOAD_ATTEMPTS}) exceeded.`);
        console.warn("Redirecting to version expired page - likely cached version issue.");
        window.location.href = "/version-expired.html";
      }
    },
    async handleCameraPermissions() {
      if (
        !this.IsNativePlatform ||
        (this.CameraPermissions !== null && this.CameraPermissions.camera === "granted")
      ) {
        return;
      }
      await Camera.requestPermissions({ permissions: ["camera"] });
      this.CameraPermissions = await Camera.checkPermissions();
    },
    async resetReloadAttemptsAfterLag(currentServerTime: Date, lagSeconds: number = 300) {
      if (this.LastReloadAttemptedOn === null) {
        return;
      }
      const timeDifference = differenceInSeconds(currentServerTime, this.LastReloadAttemptedOn);
      if (timeDifference > lagSeconds) {
        this.AppReloadAttempts = 0;
      }
    },
    resetMenus() {
      this.AppMenu = null;
      this.LatestAppVersion = null;
      this.AppMenuKey = 0;
    },
    // Bumping this key forces App.vue's <IonRouterOutlet> to remount, which is the
    // only reliable way to destroy the previous session's keep-alive cached pages
    // (Ionic's "root" navigation does NOT unmount them — framework bug #27191). Called
    // by Logout.vue AFTER it has navigated to LandingPage, so the remount happens on a
    // logged-out page and cannot re-trigger the logout flow.
    resetRouterOutlet() {
      this.RouterOutletKey += 1;
    },
    removeAppStateListeners() {
      App.removeAllListeners();
      if (this.IsNativePlatform) {
        Keyboard.removeAllListeners();
      }
    },
    handleAppReload(event: CustomEvent) {
      window.location.reload();
      (event.target as HTMLIonRefresherElement).complete();
    },
    registerScrollCallback(callback: (event: any) => void) {
      globalScrollCallbacks.add(callback);
      return () => {
        globalScrollCallbacks.delete(callback);
      };
    },
    getIonicSafeAreaInsets() {
      const computedStyles = getComputedStyle(document.documentElement);
      this.DeviceInsets = {
        Top: computedStyles.getPropertyValue("--ion-safe-area-top"),
        Bottom: computedStyles.getPropertyValue("--ion-safe-area-bottom"),
        Left: computedStyles.getPropertyValue("--ion-safe-area-left"),
        Right: computedStyles.getPropertyValue("--ion-safe-area-right"),
      };
    },
    toggleIonicSafeAreaInsets(deviceInsets: DeviceSafeAreaInsets | null) {
      if (deviceInsets === null) {
        return;
      }
      document.body.style.setProperty("--ion-safe-area-top", deviceInsets.Top);
      document.body.style.setProperty("--ion-safe-area-bottom", deviceInsets.Bottom);
      document.body.style.setProperty("--ion-safe-area-left", deviceInsets.Left);
      document.body.style.setProperty("--ion-safe-area-right", deviceInsets.Right);
    },
    setHeightFabButton() {
      document.body.style.setProperty("--fab-button-height", HeightFabButton);
    },
    triggerScrollCallbacks(event: any) {
      // Call all registered scroll callbacks
      globalScrollCallbacks.forEach((callback) => {
        callback(event);
      });
    },
    async onExitApp() {
      if (this.IsNativePlatform) {
        await App.exitApp();
      } else {
        window.close();
      }
    },
  },
  // NOTE: IsOnline, StatusBarStyle, and IsBelowSmallBreakpoint are deliberately
  // EXCLUDED from persist. IsOnline must be seeded from live navigator.onLine on
  // every hydration (initializeNetworkListener re-seeds at app mount);
  // StatusBarStyle resets to Default via router.afterEach on every navigation.
  // IsBelowSmallBreakpoint is transient device-derived state regenerated at boot
  // from matchMedia (addWindowResizeListener re-seeds at app mount); persisting
  // it would freeze a stale viewport classification across reloads. Persisting
  // any of these would cause stale-state bugs. (BR-064 for IsOnline / Wave 2
  // Task 1.1; BR-TT-005 for IsBelowSmallBreakpoint / Wave 3 Task 0.5.)
  persist: {
    omit: ["IsOnline", "StatusBarStyle", "IsBelowSmallBreakpoint"],
  },
});
